import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, serverTimestamp, getCountFromServer, limit as firestoreLimit } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { errorHandlingService } from './errorHandlingService';

export interface Integration {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  logo: string;
  status: 'available' | 'connected' | 'error' | 'syncing';
  enabled?: boolean;
  apiKey?: string;
  apiSecret?: string;
  accessToken?: string;
  refreshToken?: string;
  webhookUrl?: string;
  lastSync?: Date;
  syncFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  permissions: string[];
  fieldMappings: FieldMapping[];
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  workspaceId: string;
  contactsSynced?: number;
  /** Set by Cloud Function `shopifySyncCustomers` after catalog sync */
  productsSynced?: number;
  lastActivity?: string;
  shopDomain?: string;
}

export interface FieldMapping {
  id: string;
  integrationId: string;
  scubamailField: string;
  integrationField: string;
  fieldType: 'Contact' | 'Customer' | 'Form Submission' | 'Lead' | 'Order';
  syncDirection: 'Bidirectional' | 'From Integration' | 'To Integration';
  isActive: boolean;
  lastUpdated: Date;
  syncCount: number;
}

export interface SyncLog {
  id: string;
  integrationId: string;
  status: 'success' | 'error' | 'in_progress';
  recordsProcessed: number;
  recordsAdded: number;
  recordsUpdated: number;
  recordsFailed: number;
  errorMessage?: string;
  startedAt: Date;
  completedAt?: Date;
  duration?: number;
}

export interface WebhookEvent {
  id: string;
  integrationId: string;
  eventType: string;
  payload: any;
  processed: boolean;
  processedAt?: Date;
  createdAt: Date;
}

class IntegrationService {
  // Get all available integrations
  async getAvailableIntegrations(): Promise<Integration[]> {
    // Return static integrations for now
    return [
      {
        id: '1',
        name: 'Salesforce CRM',
        slug: 'salesforce-crm',
        category: 'CRM',
        description: 'Sync contacts and leads with Salesforce',
        logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'hourly',
        permissions: ['contacts', 'leads'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '2',
        name: 'Shopify',
        slug: 'shopify',
        category: 'E-commerce',
        description: 'Connect your Shopify store for customer data',
        logo: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'hourly',
        permissions: ['customers', 'orders'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '3',
        name: 'HubSpot',
        slug: 'hubspot',
        category: 'Marketing',
        description: 'Integrate with HubSpot for lead management',
        logo: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'hourly',
        permissions: ['contacts', 'companies'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '4',
        name: 'Zapier',
        slug: 'zapier',
        category: 'Automation',
        description: 'Connect 5000+ apps with Zapier',
        logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'realtime',
        permissions: ['webhooks'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '5',
        name: 'Google Analytics',
        slug: 'google-analytics',
        category: 'Analytics',
        description: 'Track email campaign performance',
        logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'daily',
        permissions: ['analytics'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '6',
        name: 'Facebook Pixel',
        slug: 'facebook-pixel',
        category: 'Social Media',
        description: 'Track conversions from Facebook ads',
        logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'realtime',
        permissions: ['tracking'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '7',
        name: 'Wix',
        slug: 'wix',
        category: 'Website Builder',
        description: 'Connect your Wix website for lead capture',
        logo: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
        status: 'available',
        syncFrequency: 'hourly',
        permissions: ['contacts', 'forms'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '8',
        name: 'Google Forms',
        slug: 'google-forms',
        category: 'Forms',
        description: 'Sync form submissions from Google Forms to your contact list',
        logo: 'https://www.gstatic.com/images/branding/product/1x/forms_48dp.png',
        status: 'available',
        syncFrequency: 'realtime',
        permissions: ['contacts', 'forms'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      },
      {
        id: '9',
        name: 'Typeform',
        slug: 'typeform',
        category: 'Forms',
        description: 'Sync form responses to Scubamail contacts',
        logo: 'https://www.typeform.com/favicon.ico',
        status: 'available',
        syncFrequency: 'realtime',
        permissions: ['contacts', 'forms'],
        fieldMappings: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        userId: '',
        workspaceId: '',
        contactsSynced: 0,
        lastActivity: 'Never'
      }
    ];
  }

  // Get integration by ID
  async getUserIntegrationById(integrationId: string): Promise<Integration | null> {
    try {
      const integrationDoc = await getDoc(doc(db, 'userIntegrations', integrationId));
      if (integrationDoc.exists()) {
        return {
          id: integrationDoc.id,
          ...integrationDoc.data(),
          createdAt: integrationDoc.data().createdAt?.toDate(),
          updatedAt: integrationDoc.data().updatedAt?.toDate(),
          lastSync: integrationDoc.data().lastSync?.toDate(),
        } as Integration;
      }
      return null;
    } catch (error) {
      console.error('Error getting integration by ID:', error);
      return null;
    }
  }

  // Get user's connected integrations
  async getUserIntegrations(userId: string, workspaceId: string): Promise<Integration[]> {
    try {
      console.log('📥 getUserIntegrations called:', { userId, workspaceId });
      const integrationsRef = collection(db, 'userIntegrations');
      // Note: Removed orderBy to avoid requiring composite index
      const q = query(
        integrationsRef,
        where('userId', '==', userId),
        where('workspaceId', '==', workspaceId)
      );
      console.log('📥 Executing query...');
      const snapshot = await getDocs(q);
      console.log('📥 Query returned', snapshot.docs.length, 'documents');

      const results = snapshot.docs
        .map(doc => {
          const data: any = doc.data();
          return {
            id: doc.id,
            ...data,
            // Backward compatibility for records saved by older OAuth handlers.
            slug: data.slug || data.integrationId || '',
            name: data.name || data.integrationName || '',
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            lastSync: data.lastSync?.toDate(),
          };
        })
        .filter((i: any) => i.status !== 'disconnected') as Integration[];

      // Deduplicate integrations by slug - keep the most recently updated one
      const integrationsBySlug = new Map<string, { integration: Integration; docId: string; updatedAt: Date }[]>();

      for (const integration of results) {
        const slug = integration.slug;
        if (!integrationsBySlug.has(slug)) {
          integrationsBySlug.set(slug, []);
        }
        integrationsBySlug.get(slug)!.push({
          integration,
          docId: integration.id,
          updatedAt: integration.updatedAt || integration.createdAt || new Date(0)
        });
      }

      // Find duplicates and delete older ones
      const deduplicatedResults: Integration[] = [];
      const duplicatesToDelete: string[] = [];

      // Use Array.from to convert Map entries for iteration
      const entries = Array.from(integrationsBySlug.entries());
      for (const entry of entries) {
        const slug = entry[0];
        const integrations = entry[1];
        if (integrations.length > 1) {
          console.log(`🔄 Found ${integrations.length} duplicate integrations for slug: ${slug}`);

          // Sort by updatedAt descending (newest first)
          integrations.sort((a: { updatedAt: Date }, b: { updatedAt: Date }) => b.updatedAt.getTime() - a.updatedAt.getTime());

          // Keep the newest one
          deduplicatedResults.push(integrations[0].integration);

          // Mark older ones for deletion
          for (let i = 1; i < integrations.length; i++) {
            console.log(`🗑️ Marking duplicate for deletion: ${integrations[i].docId}`);
            duplicatesToDelete.push(integrations[i].docId);
          }
        } else {
          deduplicatedResults.push(integrations[0].integration);
        }
      }

      // Delete duplicates asynchronously (don't wait for it)
      if (duplicatesToDelete.length > 0) {
        console.log(`🧹 Cleaning up ${duplicatesToDelete.length} duplicate integrations...`);
        Promise.all(
          duplicatesToDelete.map(async (docId) => {
            try {
              const docRef = doc(db, 'userIntegrations', docId);
              const { deleteDoc } = await import('firebase/firestore');
              await deleteDoc(docRef);
              console.log(`✅ Deleted duplicate integration: ${docId}`);
            } catch (err) {
              console.error(`❌ Failed to delete duplicate integration ${docId}:`, err);
            }
          })
        ).then(() => {
          console.log('🧹 Duplicate cleanup completed');
        });
      }

      console.log('📥 Returning deduplicated results:', deduplicatedResults.length);
      return deduplicatedResults;
    } catch (error) {
      console.error('❌ Error fetching user integrations:', error);
      throw error;
    }
  }

  // Connect a new integration
  async connectIntegration(integrationData: Partial<Integration>): Promise<string> {
    try {
      // Check for existing integration to prevent duplicates
      if (integrationData.userId && integrationData.workspaceId && integrationData.slug) {
        const existingIntegrations = await this.getUserIntegrations(
          integrationData.userId,
          integrationData.workspaceId
        );
        const existingIntegration = existingIntegrations.find(
          i => i.slug === integrationData.slug && i.status === 'connected'
        );

        if (existingIntegration) {
          console.log('🔄 Integration already exists, updating instead of creating new');
          // Update existing integration instead of creating duplicate
          await this.updateIntegration(existingIntegration.id, {
            ...integrationData,
            status: 'connected',
          });
          return existingIntegration.id;
        }
      }

      if (integrationData.slug === 'shopify' && integrationData.accessToken && integrationData.shopDomain) {
        try {
          console.log('🔌 Registering Shopify webhooks via Cloud Function...');
          const registerWebhooksFn = httpsCallable(functions, 'registerShopifyWebhooks');
          const result = await registerWebhooksFn({
            shopDomain: integrationData.shopDomain,
            accessToken: integrationData.accessToken
          });

          console.log('✅ Shopify webhooks registered:', result.data);
        } catch (webhookError) {
          console.error('❌ Failed to register Shopify webhooks:', webhookError);
        }
      }

      const integrationsRef = collection(db, 'userIntegrations');
      const docRef = await addDoc(integrationsRef, {
        ...integrationData,
        status: 'connected',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error connecting integration:', error);
      throw error;
    }
  }

  // Update integration configuration
  async updateIntegration(integrationId: string, updates: Partial<Integration>): Promise<void> {
    try {
      const integrationRef = doc(db, 'userIntegrations', integrationId);
      await updateDoc(integrationRef, {
        ...updates,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating integration:', error);
      throw error;
    }
  }

  // Disconnect integration
  async disconnectIntegration(integrationId: string): Promise<void> {
    try {
      const integrationRef = doc(db, 'userIntegrations', integrationId);
      await deleteDoc(integrationRef);
    } catch (error) {
      console.error('Error disconnecting integration:', error);
      throw error;
    }
  }

  // Get field mappings for an integration
  async getFieldMappings(integrationId: string): Promise<FieldMapping[]> {
    try {
      const mappingsRef = collection(db, 'fieldMappings');
      const q = query(
        mappingsRef,
        where('integrationId', '==', integrationId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastUpdated: doc.data().lastUpdated?.toDate(),
      })) as FieldMapping[];
    } catch (error) {
      console.error('Error fetching field mappings:', error);
      throw error;
    }
  }

  // Create or update field mapping
  async saveFieldMapping(mappingData: Partial<FieldMapping>): Promise<string> {
    try {
      const mappingsRef = collection(db, 'fieldMappings');
      const docRef = await addDoc(mappingsRef, {
        ...mappingData,
        lastUpdated: serverTimestamp(),
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving field mapping:', error);
      throw error;
    }
  }

  // Test integration connection
  async testConnection(integrationId: string): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const integrationRef = doc(db, 'userIntegrations', integrationId);
      const integrationDoc = await getDocs(query(collection(db, 'userIntegrations'), where('__name__', '==', integrationId)));

      if (integrationDoc.empty) {
        return { success: false, message: 'Integration not found' };
      }

      const integration = integrationDoc.docs[0].data();

      // Test connection based on integration type
      switch (integration.slug) {
        case 'salesforce-crm':
          return await this.testSalesforceConnection(integration);
        case 'shopify':
          return await this.testShopifyConnection(integration);
        case 'hubspot':
          return await this.testHubSpotConnection(integration);
        case 'wix':
          return await this.testWixConnection(integration);
        default:
          return { success: false, message: 'Unknown integration type' };
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      return { success: false, message: 'Connection test failed' };
    }
  }

  // Salesforce connection test
  private async testSalesforceConnection(integration: any): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // Simulate Salesforce API call
      const response = await fetch(`https://${integration.instanceUrl}/services/data/v58.0/sobjects/Account/describe`, {
        headers: {
          'Authorization': `Bearer ${integration.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          message: 'Salesforce connection successful',
          data: { objectCount: data.fields?.length || 0 }
        };
      } else {
        return { success: false, message: 'Salesforce authentication failed' };
      }
    } catch (error) {
      return { success: false, message: 'Salesforce connection error' };
    }
  }

  // Shopify connection test
  private async testShopifyConnection(integration: any): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // First, get shop information
      const shopResponse = await fetch(`https://${integration.shopDomain}/admin/api/2023-10/shop.json`, {
        headers: {
          'X-Shopify-Access-Token': integration.accessToken,
          'Content-Type': 'application/json',
        },
      });

      if (!shopResponse.ok) {
        return { success: false, message: 'Shopify authentication failed' };
      }

      const shopData = await shopResponse.json();

      // Test customers endpoint
      const customersResponse = await fetch(`https://${integration.shopDomain}/admin/api/2023-10/customers/count.json`, {
        headers: {
          'X-Shopify-Access-Token': integration.accessToken,
          'Content-Type': 'application/json',
        },
      });

      if (!customersResponse.ok) {
        return { success: false, message: 'Shopify API access denied' };
      }

      const customersData = await customersResponse.json();

      return {
        success: true,
        message: 'Shopify connection successful',
        data: {
          shopName: shopData.shop?.name,
          customerCount: customersData.count,
          shopDomain: integration.shopDomain
        }
      };
    } catch (error) {
      console.error('Shopify connection test error:', error);
      return { success: false, message: 'Shopify connection error' };
    }
  }

  // HubSpot connection test
  private async testHubSpotConnection(integration: any): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      // Test with contacts endpoint first
      const contactsResponse = await fetch('https://api.hubapi.com/crm/v3/objects/contacts?limit=1', {
        headers: {
          'Authorization': `Bearer ${integration.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!contactsResponse.ok) {
        return { success: false, message: 'HubSpot authentication failed' };
      }

      const contactsData = await contactsResponse.json();

      // Also test with companies endpoint to verify broader access
      const companiesResponse = await fetch('https://api.hubapi.com/crm/v3/objects/companies?limit=1', {
        headers: {
          'Authorization': `Bearer ${integration.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      const companiesData = await companiesResponse.ok ? await companiesResponse.json() : { total: 0 };

      return {
        success: true,
        message: 'HubSpot connection successful',
        data: {
          contactCount: contactsData.total || 0,
          companyCount: companiesData.total || 0,
          portalId: integration.portalId || 'N/A'
        }
      };
    } catch (error) {
      console.error('HubSpot connection test error:', error);
      return { success: false, message: 'HubSpot connection error' };
    }
  }

  // Wix connection test
  private async testWixConnection(integration: any): Promise<{ success: boolean; message: string; data?: any }> {
    try {
      const response = await fetch(`https://www.wixapis.com/site-properties/v4/properties`, {
        headers: {
          'Authorization': integration.accessToken,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          message: 'Wix connection successful',
          data: { siteId: data.siteId }
        };
      } else {
        return { success: false, message: 'Wix authentication failed' };
      }
    } catch (error) {
      return { success: false, message: 'Wix connection error' };
    }
  }

  // Sync data from integration (enhanced version)
  async syncData(integrationId: string, options?: {
    fullSync?: boolean;
    force?: boolean;
    specificFields?: string[];
  }): Promise<SyncLog> {
    try {
      // Set status to syncing immediately
      await this.updateIntegration(integrationId, { status: 'syncing' });

      // Create sync log entry
      const syncLogRef = collection(db, 'syncLogs');
      const logDoc = await addDoc(syncLogRef, {
        integrationId,
        status: 'in_progress',
        recordsProcessed: 0,
        recordsAdded: 0,
        recordsUpdated: 0,
        recordsFailed: 0,
        startedAt: serverTimestamp(),
      });

      // Get integration details
      const integrationRef = doc(db, 'userIntegrations', integrationId);
      const integrationDoc = await getDocs(query(collection(db, 'userIntegrations'), where('__name__', '==', integrationId)));

      if (integrationDoc.empty) {
        throw new Error('Integration not found');
      }

      const integration = integrationDoc.docs[0].data();

      // Perform sync based on integration type
      let syncResult;
      switch (integration.slug) {
        case 'salesforce-crm':
          syncResult = await this.syncSalesforceData(integration);
          break;
        case 'shopify':
          // Call Cloud Function to avoid CORS - Shopify API blocks browser requests
          const shopifySyncFn = httpsCallable(functions, 'shopifySyncCustomers');

          // Self-healing: Ensure webhooks are registered
          try {
            const registerWebhooksFn = httpsCallable(functions, 'registerShopifyWebhooks');
            registerWebhooksFn({
              shopDomain: integration.shopDomain,
              accessToken: integration.accessToken
            }).catch(console.error); // Fire and forget
          } catch (e) {
            console.error('Failed to initiate webhook registration', e);
          }

          const cloudResult = await shopifySyncFn({ integrationId });
          syncResult = {
            ...(cloudResult.data as any),
            startedAt: Date.now(),
          };
          break;
        case 'hubspot':
          syncResult = await this.syncHubSpotData(integration);
          break;
        case 'wix':
          syncResult = await this.syncWixData(integration);
          break;
        default:
          throw new Error('Unknown integration type');
      }

      // Update sync log
      await updateDoc(doc(db, 'syncLogs', logDoc.id), {
        status: 'success',
        recordsProcessed: syncResult.recordsProcessed,
        recordsAdded: syncResult.recordsAdded,
        recordsUpdated: syncResult.recordsUpdated,
        recordsFailed: syncResult.recordsFailed,
        completedAt: serverTimestamp(),
        duration: Date.now() - syncResult.startedAt,
      });

      // Calculate total contacts synced
      let totalContacts = 0;
      try {
        const contactsRef = collection(db, 'contacts');
        // Query by source matching the integration slug (e.g., 'shopify', 'wix')
        // Or by integrationId if available (wix uses it)
        const queries = [
          query(
            contactsRef,
            where('workspaceId', '==', integration.workspaceId),
            where('source', '==', integration.slug)
          )
        ];

        // For Wix, we also check integrationId which is more specific
        if (integration.slug === 'wix') {
          queries.push(query(
            contactsRef,
            where('workspaceId', '==', integration.workspaceId),
            where('integrationId', '==', integrationId)
          ));
        }

        // Execute count query (using the first strategy that makes sense, defaulting to source=slug)
        // Ideally we should use a consistent field. The cloud function sets 'source'='shopify'.
        // The wix sync sets 'source'='wix' AND 'integrationId'.
        const countSnapshot = await getCountFromServer(queries[0]);
        totalContacts = countSnapshot.data().count;

      } catch (countError) {
        console.error('Error counting synced contacts:', countError);
        // Fallback: if we can't count, maybe just add recordsAdded to existing? 
        // Better to leave it alone or use 0 if this fails.
        // But for now, let's assume query works.
      }

      // Update integration last sync time and stats
      await updateDoc(doc(db, 'userIntegrations', integrationId), {
        lastSync: serverTimestamp(),
        updatedAt: serverTimestamp(),
        contactsSynced: totalContacts
      });

      return {
        id: logDoc.id,
        integrationId,
        status: 'success',
        recordsProcessed: syncResult.recordsProcessed,
        recordsAdded: syncResult.recordsAdded,
        recordsUpdated: syncResult.recordsUpdated,
        recordsFailed: syncResult.recordsFailed,
        startedAt: syncResult.startedAt,
        completedAt: new Date(),
        duration: Date.now() - syncResult.startedAt,
      };
    } catch (error) {
      console.error('Error syncing data:', error);

      // Update sync log with error
      const syncLogRef = collection(db, 'syncLogs');
      await addDoc(syncLogRef, {
        integrationId,
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        startedAt: serverTimestamp(),
        completedAt: serverTimestamp(),
      });

      throw error;
    }
  }

  // Salesforce data sync
  async syncSalesforceData(integration: any): Promise<any> {
    // Implementation for Salesforce data sync
    // This would fetch contacts, leads, etc. from Salesforce
    return {
      recordsProcessed: 100,
      recordsAdded: 25,
      recordsUpdated: 75,
      recordsFailed: 0,
      startedAt: Date.now(),
    };
  }

  // Shopify data sync
  async syncShopifyData(integration: any): Promise<any> {
    console.log('🔄 Starting Shopify sync for shop:', integration.shopDomain);
    console.log('🔄 Integration data:', {
      userId: integration.userId,
      workspaceId: integration.workspaceId,
      shopDomain: integration.shopDomain,
      hasAccessToken: !!integration.accessToken
    });

    const startedAt = Date.now();
    let recordsProcessed = 0;
    let recordsAdded = 0;
    let recordsUpdated = 0;
    let recordsFailed = 0;

    try {
      // Fetch customers from Shopify
      console.log('🔄 Fetching Shopify customers from:', `https://${integration.shopDomain}/admin/api/2023-10/customers.json`);

      const customersResponse = await fetch(`https://${integration.shopDomain}/admin/api/2023-10/customers.json?limit=250`, {
        headers: {
          'X-Shopify-Access-Token': integration.accessToken,
          'Content-Type': 'application/json',
        },
      });

      console.log('🔄 Shopify API response status:', customersResponse.status);

      if (!customersResponse.ok) {
        const errorText = await customersResponse.text();
        console.error('🔄 Shopify API error:', errorText);
        throw new Error(`Failed to fetch Shopify customers: ${customersResponse.status}`);
      }

      const customersData = await customersResponse.json();
      console.log('🔄 Received customers count:', customersData.customers?.length || 0);

      // Process each customer
      for (const customer of customersData.customers || []) {
        recordsProcessed++;

        try {
          // Check if customer already exists in Scubamail
          const existingContactQuery = await import('firebase/firestore');
          const { collection, query, where, getDocs } = existingContactQuery;
          const db = (await import('../firebase')).db;

          const contactsRef = collection(db, 'contacts');
          const existingQuery = query(
            contactsRef,
            where('userId', '==', integration.userId),
            where('workspaceId', '==', integration.workspaceId),
            where('email', '==', customer.email)
          );

          const existingSnapshot = await getDocs(existingQuery);

          const contactData = {
            email: customer.email,
            firstName: customer.first_name || '',
            lastName: customer.last_name || '',
            phone: customer.phone || '',
            tags: customer.tags || [],
            segments: [],
            customFields: {
              shopifyCustomerId: customer.id.toString(),
              shopifyCreatedAt: customer.created_at,
              shopifyUpdatedAt: customer.updated_at,
              totalSpent: customer.total_spent || '0',
              ordersCount: customer.orders_count || 0,
              lastOrderDate: customer.last_order_date || null,
            },
            userId: integration.userId,
            workspaceId: integration.workspaceId,
            status: 'subscribed',
            source: 'shopify',
            integrationId: integration.id,
          };

          if (!existingSnapshot.empty) {
            // Update existing contact
            const existingDoc = existingSnapshot.docs[0];
            await updateDoc(existingDoc.ref, contactData);
            recordsUpdated++;
          } else {
            // Create new contact
            await addDoc(contactsRef, contactData);
            recordsAdded++;
          }
        } catch (contactError) {
          console.error(`Error processing Shopify customer ${customer.email}:`, contactError);
          recordsFailed++;
        }
      }

      return {
        recordsProcessed,
        recordsAdded,
        recordsUpdated,
        recordsFailed,
        startedAt,
      };
    } catch (error) {
      console.error('Error syncing Shopify data:', error);
      throw error;
    }
  }

  // HubSpot data sync
  async syncHubSpotData(integration: any): Promise<any> {
    const startedAt = Date.now();
    let recordsProcessed = 0;
    let recordsAdded = 0;
    let recordsUpdated = 0;
    let recordsFailed = 0;

    try {
      // Fetch contacts from HubSpot
      const contactsResponse = await fetch(`https://api.hubapi.com/crm/v3/objects/contacts?limit=100&properties=firstname,lastname,email,phone,company,lifecyclestage,hs_object_id,createdate,hs_lastmodifieddate`, {
        headers: {
          'Authorization': `Bearer ${integration.accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!contactsResponse.ok) {
        throw new Error(`Failed to fetch HubSpot contacts: ${contactsResponse.status}`);
      }

      const contactsData = await contactsResponse.json();

      // Process each contact
      for (const contact of contactsData.results || []) {
        recordsProcessed++;

        try {
          const properties = contact.properties || {};

          // Check if contact already exists in Scubamail
          const existingContactQuery = await import('firebase/firestore');
          const { collection, query, where, getDocs } = existingContactQuery;
          const db = (await import('../firebase')).db;

          const contactsRef = collection(db, 'contacts');
          const existingQuery = query(
            contactsRef,
            where('userId', '==', integration.userId),
            where('workspaceId', '==', integration.workspaceId),
            where('email', '==', properties.email)
          );

          const existingSnapshot = await getDocs(existingQuery);

          const contactData = {
            email: properties.email || '',
            firstName: properties.firstname || '',
            lastName: properties.lastname || '',
            phone: properties.phone || '',
            tags: properties.lifecyclestage ? [properties.lifecyclestage] : [],
            segments: [],
            customFields: {
              hubspotContactId: properties.hs_object_id?.toString() || '',
              hubspotCreatedAt: properties.createdate || '',
              hubspotUpdatedAt: properties.hs_lastmodifieddate || '',
              hubspotCompany: properties.company || '',
              hubspotLifecycleStage: properties.lifecyclestage || '',
            },
            userId: integration.userId,
            workspaceId: integration.workspaceId,
            status: 'subscribed',
          };

          if (!existingSnapshot.empty) {
            // Update existing contact
            const existingDoc = existingSnapshot.docs[0];
            await updateDoc(existingDoc.ref, contactData);
            recordsUpdated++;
          } else {
            // Create new contact
            await addDoc(contactsRef, contactData);
            recordsAdded++;
          }
        } catch (contactError) {
          console.error(`Error processing HubSpot contact ${contact.properties?.email}:`, contactError);
          recordsFailed++;
        }
      }

      // Also sync companies if we have company data
      if (integration.permissions?.includes('companies')) {
        try {
          const companiesResponse = await fetch(`https://api.hubapi.com/crm/v3/objects/companies?limit=50&properties=name,domain,industry,hs_object_id,createdate,hs_lastmodifieddate`, {
            headers: {
              'Authorization': `Bearer ${integration.accessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (companiesResponse.ok) {
            const companiesData = await companiesResponse.json();

            for (const company of companiesData.results || []) {
              recordsProcessed++;

              try {
                const properties = company.properties || {};

                // For companies, we could create organization records or link to existing contacts
                // For now, we'll just log the company data
                console.log('HubSpot company synced:', properties.name, properties.domain);

                // In a full implementation, you might want to:
                // 1. Create organization/company records in Scubamail
                // 2. Link contacts to their companies
                // 3. Update contact records with company information

              } catch (companyError) {
                console.error(`Error processing HubSpot company ${company.properties?.name}:`, companyError);
                recordsFailed++;
              }
            }
          }
        } catch (companyError) {
          console.error('Error syncing HubSpot companies:', companyError);
        }
      }

      return {
        recordsProcessed,
        recordsAdded,
        recordsUpdated,
        recordsFailed,
        startedAt,
      };
    } catch (error) {
      console.error('Error syncing HubSpot data:', error);
      throw error;
    }
  }

  // Wix data sync
  async syncWixData(integration: any): Promise<any> {
    const startedAt = Date.now();
    let recordsProcessed = 0;
    let recordsAdded = 0;
    let recordsUpdated = 0;
    let recordsFailed = 0;

    try {
      if (!integration.accessToken) {
        throw new Error('Wix access token not found');
      }

      const instanceId = integration.instanceId || integration.metaSiteId;
      if (!instanceId) {
        throw new Error('Wix instance ID not found');
      }

      const baseUrl = `https://www.wixapis.com`;
      const headers = {
        'Authorization': integration.accessToken,
        'Content-Type': 'application/json',
        'wix-instance-id': instanceId,
      };

      // 1. Sync Wix Contacts
      try {
        const contactsResponse = await fetch(`${baseUrl}/contacts/v4/contacts?paging.limit=100`, {
          headers,
        });

        if (contactsResponse.ok) {
          const contactsData = await contactsResponse.json();
          const contacts = contactsData.contacts || [];

          for (const contact of contacts) {
            recordsProcessed++;
            try {
              const contactData = {
                email: contact.info?.email || contact.emails?.[0]?.email || '',
                firstName: contact.info?.firstName || contact.name?.first || '',
                lastName: contact.info?.lastName || contact.name?.last || '',
                phone: contact.info?.phone || contact.phones?.[0]?.phone || '',
                tags: contact.tags || [],
                segments: [],
                customFields: {
                  wixContactId: contact.id,
                  wixCreatedAt: contact.createdDate,
                  wixUpdatedAt: contact.lastUpdatedDate,
                  wixSource: contact.source || 'wix',
                },
                userId: integration.userId,
                workspaceId: integration.workspaceId,
                status: contact.subscriptionStatus === 'SUBSCRIBED' ? 'subscribed' : 'unsubscribed',
                source: 'wix',
                externalId: contact.id,
                integrationId: integration.id,
                lastSynced: new Date(),
              };

              if (!contactData.email) {
                recordsFailed++;
                continue;
              }

              // Check if contact already exists
              const contactsRef = collection(db, 'contacts');
              const existingQuery = query(
                contactsRef,
                where('userId', '==', integration.userId),
                where('workspaceId', '==', integration.workspaceId),
                where('email', '==', contactData.email.toLowerCase())
              );

              const existingSnapshot = await getDocs(existingQuery);

              if (!existingSnapshot.empty) {
                // Update existing contact
                const existingDoc = existingSnapshot.docs[0];
                await updateDoc(existingDoc.ref, {
                  ...contactData,
                  updatedAt: serverTimestamp(),
                });
                recordsUpdated++;
              } else {
                // Create new contact
                await addDoc(contactsRef, {
                  ...contactData,
                  addedAt: serverTimestamp(),
                  lastChanged: serverTimestamp(),
                });
                recordsAdded++;
              }
            } catch (contactError) {
              console.error(`Error processing Wix contact ${contact.id}:`, contactError);
              recordsFailed++;
            }
          }
        }
      } catch (contactsError) {
        console.error('Error syncing Wix contacts:', contactsError);
        // Continue with other syncs even if contacts fail
      }

      // 2. Sync Wix Form Submissions
      try {
        const formsResponse = await fetch(`${baseUrl}/forms/v1/submissions?paging.limit=100`, {
          headers,
        });

        if (formsResponse.ok) {
          const formsData = await formsResponse.json();
          const submissions = formsData.submissions || [];

          for (const submission of submissions) {
            recordsProcessed++;
            try {
              const email = submission.data?.find((field: any) =>
                field.fieldName?.toLowerCase().includes('email') ||
                field.fieldName?.toLowerCase() === 'email'
              )?.value || '';

              if (!email || !this.isValidEmail(email)) {
                recordsFailed++;
                continue;
              }

              const contactData = {
                email: email.toLowerCase(),
                firstName: submission.data?.find((field: any) =>
                  field.fieldName?.toLowerCase().includes('first') ||
                  field.fieldName?.toLowerCase() === 'firstname'
                )?.value || '',
                lastName: submission.data?.find((field: any) =>
                  field.fieldName?.toLowerCase().includes('last') ||
                  field.fieldName?.toLowerCase() === 'lastname'
                )?.value || '',
                phone: submission.data?.find((field: any) =>
                  field.fieldName?.toLowerCase().includes('phone')
                )?.value || '',
                tags: ['wix-form-submission'],
                segments: [],
                customFields: {
                  wixSubmissionId: submission.id,
                  wixFormId: submission.formId,
                  wixFormName: submission.formName,
                  wixSubmittedAt: submission.submittedAt,
                  source: 'wix-form',
                },
                userId: integration.userId,
                workspaceId: integration.workspaceId,
                status: 'subscribed',
                source: 'wix',
                externalId: submission.id,
                integrationId: integration.id,
                lastSynced: new Date(),
              };

              // Check if contact already exists
              const contactsRef = collection(db, 'contacts');
              const existingQuery = query(
                contactsRef,
                where('userId', '==', integration.userId),
                where('workspaceId', '==', integration.workspaceId),
                where('email', '==', contactData.email)
              );

              const existingSnapshot = await getDocs(existingQuery);

              if (!existingSnapshot.empty) {
                // Update existing contact
                const existingDoc = existingSnapshot.docs[0];
                await updateDoc(existingDoc.ref, {
                  ...contactData,
                  updatedAt: serverTimestamp(),
                });
                recordsUpdated++;
              } else {
                // Create new contact
                await addDoc(contactsRef, {
                  ...contactData,
                  addedAt: serverTimestamp(),
                  lastChanged: serverTimestamp(),
                });
                recordsAdded++;
              }
            } catch (submissionError) {
              console.error(`Error processing Wix form submission ${submission.id}:`, submissionError);
              recordsFailed++;
            }
          }
        }
      } catch (formsError) {
        console.error('Error syncing Wix form submissions:', formsError);
        // Continue even if forms fail
      }

      // 3. Sync Wix Stores Orders/Customers (if Wix Stores is enabled)
      try {
        const ordersResponse = await fetch(`${baseUrl}/stores/v1/orders?paging.limit=100`, {
          headers,
        });

        if (ordersResponse.ok) {
          const ordersData = await ordersResponse.json();
          const orders = ordersData.orders || [];

          for (const order of orders) {
            recordsProcessed++;
            try {
              const buyerInfo = order.buyerInfo || order.billingInfo || {};
              const email = buyerInfo.email || order.buyer?.email || '';

              if (!email || !this.isValidEmail(email)) {
                recordsFailed++;
                continue;
              }

              const contactData = {
                email: email.toLowerCase(),
                firstName: buyerInfo.firstName || order.buyer?.firstName || '',
                lastName: buyerInfo.lastName || order.buyer?.lastName || '',
                phone: buyerInfo.phone || order.buyer?.phone || '',
                tags: ['wix-customer', 'wix-store-order'],
                segments: [],
                customFields: {
                  wixOrderId: order.id,
                  wixOrderNumber: order.number,
                  wixOrderTotal: order.priceSummary?.total?.amount || 0,
                  wixOrderCurrency: order.currency || 'USD',
                  wixOrderDate: order.createdDate,
                  wixOrderStatus: order.status,
                  source: 'wix-store',
                },
                userId: integration.userId,
                workspaceId: integration.workspaceId,
                status: 'subscribed',
                source: 'wix',
                externalId: order.buyer?.contactId || order.id,
                integrationId: integration.id,
                lastSynced: new Date(),
              };

              // Check if contact already exists
              const contactsRef = collection(db, 'contacts');
              const existingQuery = query(
                contactsRef,
                where('userId', '==', integration.userId),
                where('workspaceId', '==', integration.workspaceId),
                where('email', '==', contactData.email)
              );

              const existingSnapshot = await getDocs(existingQuery);

              if (!existingSnapshot.empty) {
                // Update existing contact
                const existingDoc = existingSnapshot.docs[0];
                await updateDoc(existingDoc.ref, {
                  ...contactData,
                  updatedAt: serverTimestamp(),
                });
                recordsUpdated++;
              } else {
                // Create new contact
                await addDoc(contactsRef, {
                  ...contactData,
                  addedAt: serverTimestamp(),
                  lastChanged: serverTimestamp(),
                });
                recordsAdded++;
              }
            } catch (orderError) {
              console.error(`Error processing Wix order ${order.id}:`, orderError);
              recordsFailed++;
            }
          }
        }
      } catch (ordersError) {
        console.error('Error syncing Wix orders:', ordersError);
        // Continue even if orders fail
      }

      return {
        recordsProcessed,
        recordsAdded,
        recordsUpdated,
        recordsFailed,
        startedAt,
      };
    } catch (error) {
      console.error('Error syncing Wix data:', error);
      throw error;
    }
  }

  // Helper method to validate email
  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Get sync logs for an integration
  async getSyncLogs(integrationId: string, limit: number = 10): Promise<SyncLog[]> {
    try {
      const syncLogsRef = collection(db, 'syncLogs');
      const q = query(
        syncLogsRef,
        where('integrationId', '==', integrationId),
        orderBy('startedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.slice(0, limit).map(doc => ({
        id: doc.id,
        ...doc.data(),
        startedAt: doc.data().startedAt?.toDate(),
        completedAt: doc.data().completedAt?.toDate(),
      })) as SyncLog[];
    } catch (error) {
      console.error('Error fetching sync logs:', error);
      throw error;
    }
  }

  // Listen to real-time integration updates
  subscribeToIntegrations(userId: string, workspaceId: string, callback: (integrations: Integration[]) => void) {
    const integrationsRef = collection(db, 'userIntegrations');
    const q = query(
      integrationsRef,
      where('userId', '==', userId),
      where('workspaceId', '==', workspaceId),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
      const integrations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        lastSync: doc.data().lastSync?.toDate(),
      })) as Integration[];
      callback(integrations);
    });
  }

  // Get e-commerce stats for a workspace (orders, revenue, abandoned carts)
  async getEcommerceStats(workspaceId: string, userId: string): Promise<{
    ordersLast30Days: number;
    revenueLast30Days: number;
    abandonedCartsLast7Days: number;
    lastEventTimestamp: Date | null;
  }> {
    try {
      if (!userId) {
        return {
          ordersLast30Days: 0,
          revenueLast30Days: 0,
          abandonedCartsLast7Days: 0,
          lastEventTimestamp: null,
        };
      }

      const eventsRef = collection(db, 'ecommerce_events');
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      // Fetch events for this workspace that belong to the signed-in user (matches security rules)
      const q = query(
        eventsRef,
        where('workspaceId', '==', workspaceId),
        where('userId', '==', userId)
      );
      const snapshot = await getDocs(q);

      let ordersLast30Days = 0;
      let revenueLast30Days = 0;
      let abandonedCartsLast7Days = 0;
      let lastEventTimestamp: Date | null = null;

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const eventDate = data.createdAt?.toDate?.() || new Date(data.createdAt);

        // Track latest event
        if (!lastEventTimestamp || eventDate > lastEventTimestamp) {
          lastEventTimestamp = eventDate;
        }

        // Count orders in last 30 days
        if (data.eventType === 'placed_order' && eventDate >= thirtyDaysAgo) {
          ordersLast30Days++;
          revenueLast30Days += parseFloat(data.orderTotal || data.cart?.cartValue || 0);
        }

        // Count abandoned carts in last 7 days (added_product_to_cart or started_checkout without order)
        if (
          (data.eventType === 'added_product_to_cart' || data.eventType === 'started_checkout') &&
          eventDate >= sevenDaysAgo
        ) {
          abandonedCartsLast7Days++;
        }
      });

      return {
        ordersLast30Days,
        revenueLast30Days: Math.round(revenueLast30Days * 100) / 100,
        abandonedCartsLast7Days,
        lastEventTimestamp,
      };
    } catch (error) {
      console.error('Error fetching e-commerce stats:', error);
      return {
        ordersLast30Days: 0,
        revenueLast30Days: 0,
        abandonedCartsLast7Days: 0,
        lastEventTimestamp: null,
      };
    }
  }

  // Get recent Shopify orders with details
  async getShopifyOrders(workspaceId: string, limit: number = 20, userId?: string): Promise<{
    id: string;
    orderNumber: string;
    customerEmail: string;
    customerName: string;
    orderTotal: number;
    currency: string;
    orderStatus: string;
    fulfillmentStatus: string;
    itemCount: number;
    orderDate: string;
    lineItems: {
      title: string;
      variantTitle: string;
      quantity: number;
      price: number;
    }[];
  }[]> {
    try {
      if (!userId) {
        return [];
      }

      const eventsRef = collection(db, 'ecommerce_events');
      // Note: Removed orderBy('createdAt', 'desc') to avoid needing a composite index
      // which might not be created yet. We sort in memory instead.
      // limit(200) caps reads — callers slice to their own limit afterwards.
      const q = query(
        eventsRef,
        where('workspaceId', '==', workspaceId),
        where('eventType', '==', 'placed_order'),
        where('userId', '==', userId),
        firestoreLimit(200)  // cap read cost; client slices to its own display limit below
      );
      const snapshot = await getDocs(q);

      const orders = snapshot.docs.map(doc => {
        const data = doc.data();
        const rawLineItems = data.lineItems || [];
        const lineItems = Array.isArray(rawLineItems)
          ? rawLineItems.map((item: any) => ({
              title: item.title || item.productTitle || '',
              variantTitle: item.variantTitle || '',
              quantity: item.quantity || 0,
              price: item.price || 0,
            }))
          : [];

        const itemCount =
          typeof data.itemCount === 'number' && data.itemCount > 0
            ? data.itemCount
            : lineItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

        return {
          id: doc.id,
          orderNumber: data.orderNumber || '',
          customerEmail: data.customerEmail || '',
          customerName: data.customerName || '',
          orderTotal: data.orderTotal || 0,
          currency: data.currency || 'USD',
          orderStatus: data.orderStatus || 'pending',
          fulfillmentStatus: data.fulfillmentStatus || 'unfulfilled',
          itemCount,
          orderDate: data.orderDate || '',
          lineItems,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now())
        };
      });

      // Sort by createdAt descending and take limit
      return orders
        .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
        .slice(0, limit);

    } catch (error) {
      console.error('Error fetching Shopify orders:', error);
      return [];
    }
  }

  // Get Shopify-sourced contacts for the current workspace/user
  async getShopifyCustomers(
    workspaceId: string,
    userId: string,
    limit: number = 100
  ): Promise<Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    status: string;
    totalSpent: string;
    ordersCount: number;
    updatedAt: Date | null;
  }>> {
    try {
      if (!workspaceId || !userId) return [];

      const contactsRef = collection(db, 'contacts');
      const q = query(
        contactsRef,
        where('workspaceId', '==', workspaceId),
        where('userId', '==', userId),
        where('source', '==', 'shopify')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs
        .map((d) => {
          const data: any = d.data();
          return {
            id: d.id,
            email: data.email || '',
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            status: data.status || 'subscribed',
            totalSpent: data.customFields?.totalSpent || '0',
            ordersCount: Number(data.customFields?.ordersCount || 0),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : null,
          };
        })
        .sort((a, b) => {
          const at = a.updatedAt ? a.updatedAt.getTime() : 0;
          const bt = b.updatedAt ? b.updatedAt.getTime() : 0;
          return bt - at;
        })
        .slice(0, Math.max(limit, 1));
    } catch (error) {
      console.error('Error fetching Shopify customers:', error);
      return [];
    }
  }

  // Get abandoned cart events for the current workspace/user
  async getShopifyAbandonedCarts(
    workspaceId: string,
    userId: string,
    limit: number = 50
  ): Promise<Array<{
    id: string;
    contactId: string;
    abandonedCheckoutURL: string;
    cartId: string;
    cartValue: number;
    currency: string;
    itemCount: number;
    createdAt: Date | null;
  }>> {
    try {
      if (!workspaceId || !userId) return [];

      const eventsRef = collection(db, 'ecommerce_events');
      const q = query(
        eventsRef,
        where('workspaceId', '==', workspaceId),
        where('userId', '==', userId),
        where('eventType', '==', 'abandoned_cart'),
        firestoreLimit(200)  // cap read cost; client slices to its own display limit below
      );
      const snapshot = await getDocs(q);

      return snapshot.docs
        .map((d) => {
          const data: any = d.data();
          const cart = data.cart || {};
          return {
            id: d.id,
            contactId: data.contactId || '',
            abandonedCheckoutURL: data.abandonedCheckoutURL || '',
            cartId: cart.cartId || '',
            cartValue: Number(cart.cartValue || 0),
            currency: cart.currency || 'USD',
            itemCount: Number(cart.itemCount || 0),
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : null,
          };
        })
        .sort((a, b) => {
          const at = a.createdAt ? a.createdAt.getTime() : 0;
          const bt = b.createdAt ? b.createdAt.getTime() : 0;
          return bt - at;
        })
        .slice(0, Math.max(limit, 1));
    } catch (error) {
      console.error('Error fetching abandoned carts:', error);
      return [];
    }
  }

  async cleanupDuplicateIntegrations(): Promise<any> {
    try {
      const cleanupFn = httpsCallable(functions, 'runCleanup');
      const result = await cleanupFn();
      return result.data;
    } catch (error) {
      console.error('Error cleaning up duplicate integrations:', error);
      throw error;
    }
  }
}

// Enhanced Sync Engine for real-time and scheduled syncing
class SyncEngine {
  private syncQueue: Map<string, NodeJS.Timeout> = new Map();
  private activeSyncs: Set<string> = new Set();
  private syncIntervals: Map<string, NodeJS.Timeout> = new Map();

  // Start real-time sync for an integration
  async startRealTimeSync(integrationId: string, intervalMinutes: number = 5): Promise<void> {
    // Clear existing interval if any
    this.stopSync(integrationId);

    const intervalMs = intervalMinutes * 60 * 1000;
    const intervalId = setInterval(async () => {
      try {
        if (!this.activeSyncs.has(integrationId)) {
          await this.performSyncInternal(integrationId, { fullSync: false });
        }
      } catch (error) {
        console.error(`Real-time sync failed for integration ${integrationId}:`, error);
      }
    }, intervalMs);

    this.syncIntervals.set(integrationId, intervalId);
    console.log(`Started real-time sync for integration ${integrationId} (every ${intervalMinutes} minutes)`);
  }

  // Stop sync for an integration
  stopSync(integrationId: string): void {
    // Clear interval
    const intervalId = this.syncIntervals.get(integrationId);
    if (intervalId) {
      clearInterval(intervalId);
      this.syncIntervals.delete(integrationId);
    }

    // Clear any queued sync
    const queueId = this.syncQueue.get(integrationId);
    if (queueId) {
      clearTimeout(queueId);
      this.syncQueue.delete(integrationId);
    }

    // Remove from active syncs
    this.activeSyncs.delete(integrationId);

    console.log(`Stopped sync for integration ${integrationId}`);
  }

  // Queue a sync operation (prevents overlapping syncs)
  async queueSync(integrationId: string, delayMs: number = 0): Promise<void> {
    // Clear existing queue entry
    const existingQueueId = this.syncQueue.get(integrationId);
    if (existingQueueId) {
      clearTimeout(existingQueueId);
    }

    const queueId = setTimeout(async () => {
      try {
        this.syncQueue.delete(integrationId);
        if (!this.activeSyncs.has(integrationId)) {
          await this.performSyncInternal(integrationId, { fullSync: false });
        }
      } catch (error) {
        console.error(`Queued sync failed for integration ${integrationId}:`, error);
      }
    }, delayMs);

    this.syncQueue.set(integrationId, queueId);
  }

  // Perform actual sync operation (public method for external access)
  async performSync(integrationId: string, options: { fullSync?: boolean; force?: boolean; specificFields?: string[] }): Promise<void> {
    return this.performSyncInternal(integrationId, options);
  }

  // Internal sync operation (private implementation)
  private async performSyncInternal(integrationId: string, options: { fullSync?: boolean; force?: boolean; specificFields?: string[] }): Promise<void> {
    if (this.activeSyncs.has(integrationId)) {
      console.log(`Sync already in progress for integration ${integrationId}`);
      return;
    }

    this.activeSyncs.add(integrationId);

    try {
      console.log(`Starting sync for integration ${integrationId}`);

      // Get integration details
      const integration = await integrationService.getUserIntegrationById(integrationId);
      if (!integration) {
        throw new Error(`Integration ${integrationId} not found`);
      }

      // Check if integration is enabled
      if (!integration.enabled) {
        console.log(`Integration ${integrationId} is disabled, skipping sync`);
        return;
      }

      // Perform sync based on integration type
      let syncResult;
      try {
        switch (integration.slug) {
          case 'shopify':
            syncResult = await integrationService.syncShopifyData(integration);
            break;
          case 'hubspot':
            syncResult = await integrationService.syncHubSpotData(integration);
            break;
          case 'salesforce-crm':
            syncResult = await integrationService.syncSalesforceData(integration);
            break;
          case 'wix':
            syncResult = await integrationService.syncWixData(integration);
            break;
          default:
            throw new Error(`Unsupported integration type: ${integration.slug}`);
        }
      } catch (syncError) {
        // Log sync error
        await errorHandlingService.logError(
          integrationId,
          'SYNC_ERROR',
          syncError instanceof Error ? syncError : new Error(String(syncError)),
          { integration: integration.name, platform: integration.slug, options },
          'HIGH'
        );
        throw syncError;
      }

      // Update integration last sync time
      await updateDoc(doc(db, 'userIntegrations', integrationId), {
        lastSync: serverTimestamp(),
        updatedAt: serverTimestamp(),
        contactsSynced: (integration.contactsSynced || 0) + syncResult.recordsAdded,
      });

      console.log(`Sync completed for integration ${integrationId}:`, syncResult);

    } catch (error) {
      console.error(`Sync failed for integration ${integrationId}:`, error);

      // Update integration with error status
      await updateDoc(doc(db, 'userIntegrations', integrationId), {
        status: 'error',
        updatedAt: serverTimestamp(),
        lastError: error instanceof Error ? error.message : 'Unknown sync error',
      });
    } finally {
      this.activeSyncs.delete(integrationId);
    }
  }

  // Get integration by ID (helper method)
  private async getIntegrationById(integrationId: string): Promise<Integration | null> {
    try {
      const integrationDoc = await getDoc(doc(db, 'userIntegrations', integrationId));
      if (integrationDoc.exists()) {
        return {
          id: integrationDoc.id,
          ...integrationDoc.data(),
          createdAt: integrationDoc.data().createdAt?.toDate(),
          updatedAt: integrationDoc.data().updatedAt?.toDate(),
          lastSync: integrationDoc.data().lastSync?.toDate(),
        } as Integration;
      }
      return null;
    } catch (error) {
      console.error('Error getting integration:', error);
      return null;
    }
  }

  // Schedule sync for specific time
  scheduleSync(integrationId: string, cronExpression: string): void {
    // This would implement cron-like scheduling
    // For now, we'll use simple interval-based scheduling
    const [minutes] = cronExpression.split(' ');
    const intervalMinutes = parseInt(minutes) || 60; // Default to hourly

    this.startRealTimeSync(integrationId, intervalMinutes);
  }

  // Get sync status for all integrations
  getSyncStatus(): {
    activeSyncs: string[];
    queuedSyncs: string[];
    scheduledSyncs: string[];
  } {
    return {
      activeSyncs: Array.from(this.activeSyncs),
      queuedSyncs: Array.from(this.syncQueue.keys()),
      scheduledSyncs: Array.from(this.syncIntervals.keys()),
    };
  }

  // Force sync all integrations
  async syncAllIntegrations(userId: string, workspaceId: string): Promise<void> {
    try {
      const integrations = await integrationService.getUserIntegrations(userId, workspaceId);

      for (const integration of integrations) {
        if (integration.status === 'connected' && (integration as any).enabled) {
          await this.performSyncInternal(integration.id, { fullSync: true, force: true });
        }
      }
    } catch (error) {
      console.error('Error syncing all integrations:', error);
      throw error;
    }
  }
}

// Create sync engine instance
export const syncEngine = new SyncEngine();

export const integrationService = new IntegrationService();
